const ELIGIBLE_STATES = new Set([
  'RECIBIDA_DESTINO',
  'CONFIRMADA_LOCAL',
  'RETENIDA_REPROGRAMABLE',
  'RECIBIDA',
  'REPROGRAMADA',
]);

const BLOCKED_STATES = new Set([
  'ENTREGADA',
  'ANULADA',
  'EN_REPARTO',
  'EN_FOJA',
  'FOJA_CERRADA',
  'EN_TRANSITO',
  'PENDIENTE_RECANALIZACION',
  'EN_TRANSITO_A_DESTINO',
  'EN_TRANSITO_A_VIA',
  'RECIBIDA_EN_VIA',
]);

const RETRY_STATES = new Set(['RETENIDA_REPROGRAMABLE', 'REPROGRAMADA', 'RETENIDA']);

function pickFirst(...values) {
  return values.find((value) => value !== null && value !== undefined && value !== '') ?? null;
}

export function normalizeRepartoGuideState(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
}

export function getGuideNumericValue(guide, keys, fallback = 0) {
  for (const key of keys) {
    const parsed = Number(guide?.[key]);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

export function getGuideNumber(guide) {
  return pickFirst(guide?.nro_guia, guide?.numero_guia, guide?.id) || 'Sin guia';
}

export function getGuideRecipient(guide) {
  return pickFirst(
    guide?.destinatario,
    guide?.destinatario_nombre,
    guide?.destinatario_razon_social,
  ) || 'Sin destinatario';
}

export function getGuideLocality(guide) {
  return pickFirst(
    guide?.localidad_destino,
    guide?.localidad_destino_nombre,
    guide?.destino,
    guide?.destino_nombre,
    guide?.zona,
  ) || 'Sin localidad';
}

export function getGuideAddress(guide) {
  return pickFirst(
    guide?.domicilio,
    guide?.domicilio_destinatario,
    guide?.direccion,
  ) || '';
}

export function getGuideDeclaredValue(guide) {
  return getGuideNumericValue(guide, ['valor_declarado', 'valorDeclarado', 'valor_decl', 'vd'], 0);
}

export function getGuideCashOnDelivery(guide) {
  return getGuideNumericValue(guide, ['contrareembolso', 'contra_reembolso', 'cr'], 0);
}

export function getGuideSearchText(guide) {
  return [
    guide?.nro_guia,
    guide?.numero_guia,
    guide?.remito,
    guide?.remito_numero,
    guide?.destinatario,
    guide?.destinatario_nombre,
    guide?.cliente,
    guide?.cliente_nombre,
    guide?.pagador,
    guide?.localidad_destino,
    guide?.localidad,
    guide?.domicilio,
    guide?.linea_operativa,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function getGuideBranchId(guide) {
  return pickFirst(
    guide?.sucursal_actual_id,
    guide?.destino_sucursal_id,
    guide?.sucursal_destino_id,
    guide?.sucursal_id,
  );
}

export function isGuideRetry(guide) {
  return RETRY_STATES.has(normalizeRepartoGuideState(guide?.estado));
}

export function evaluateGuideEligibility(guide, context = {}) {
  const state = normalizeRepartoGuideState(guide?.estado || guide?.estado_logistico);
  const branchId = Number(context?.sucursalId || 0);
  const currentBranchId = Number(getGuideBranchId(guide) || 0);
  const activeRepartoId = pickFirst(guide?.reparto_activo_id, guide?.reparto_id, guide?.repartoId);
  const pendingRecanalization = Boolean(guide?.pendiente_recanalizacion);
  const recipient = getGuideRecipient(guide);
  const locality = getGuideLocality(guide);
  const address = getGuideAddress(guide);

  if (!state) {
    return { eligible: false, reason: 'Sin estado operativo', code: 'sin_estado', state: 'SIN_ESTADO' };
  }

  if (pendingRecanalization || state === 'PENDIENTE_RECANALIZACION') {
    return { eligible: false, reason: 'Pendiente de recanalizacion', code: 'pendiente_recanalizacion', state };
  }

  if (branchId > 0 && currentBranchId > 0 && currentBranchId !== branchId) {
    return { eligible: false, reason: 'Pertenece a otra sucursal', code: 'otra_sucursal', state };
  }

  if (activeRepartoId && Number(activeRepartoId) > 0) {
    return { eligible: false, reason: 'Ya esta en reparto activo', code: 'reparto_activo', state };
  }

  if (state === 'RETENIDA') {
    return { eligible: false, reason: 'Retenida sin reprogramacion', code: 'retenida_cerrada', state };
  }

  if (BLOCKED_STATES.has(state)) {
    if (state === 'ANULADA' || state === 'ENTREGADA') {
      return { eligible: false, reason: 'Anulada/entregada', code: 'estado_final', state };
    }

    if (state.startsWith('EN_TRANSITO') || state === 'RECIBIDA_EN_VIA') {
      return { eligible: false, reason: 'No recibida en destino', code: 'pendiente_recepcion', state };
    }

    return { eligible: false, reason: 'Estado no repartible', code: 'estado_no_repartible', state };
  }

  if (!ELIGIBLE_STATES.has(state)) {
    return { eligible: false, reason: 'No recibida en destino', code: 'no_recibida_destino', state };
  }

  if (!recipient || recipient === 'Sin destinatario') {
    return { eligible: false, reason: 'Falta destinatario', code: 'faltan_datos_destinatario', state };
  }

  if (!locality || locality === 'Sin localidad') {
    return { eligible: false, reason: 'Falta localidad', code: 'faltan_datos_localidad', state };
  }

  if (!address) {
    return { eligible: false, reason: 'Falta domicilio', code: 'faltan_datos_domicilio', state };
  }

  return { eligible: true, reason: '', code: 'ok', state };
}

/** Normalizes guide rows from /api/guias (and optional metrics rows) into one row per guide. */
export function mergeGuideSources(baseRows = [], metricsRows = []) {
  const merged = new Map();

  baseRows.forEach((row) => {
    merged.set(Number(row.id), {
      ...row,
      id: Number(row.id),
      nro_guia: row.nro_guia || row.numero_guia || String(row.id),
      numero_guia: row.numero_guia || row.nro_guia || String(row.id),
      remitente_nombre: row.remitente_nombre || row.remitente || row.cliente_nombre || row.cliente || null,
      destinatario_nombre: row.destinatario_nombre || row.destinatario || null,
      localidad_destino: row.localidad_destino || row.localidad_destino_nombre || row.destino || row.destino_nombre || null,
      domicilio: row.domicilio || row.domicilio_destinatario || null,
      bultos: getGuideNumericValue(row, ['bultos'], 0),
      kg_reales: getGuideNumericValue(row, ['kg_reales', 'kilos'], 0),
      m3: getGuideNumericValue(row, ['m3'], 0),
      valor_declarado: getGuideDeclaredValue(row),
      contrareembolso: getGuideCashOnDelivery(row),
    });
  });

  metricsRows.forEach((row) => {
    const id = Number(row.id);
    const existing = merged.get(id) || {
      id,
      nro_guia: row.numero_guia || `#${id}`,
      numero_guia: row.numero_guia || `#${id}`,
      estado: row.estado || null,
    };

    merged.set(id, {
      ...existing,
      ...row,
      id,
      nro_guia: existing.nro_guia || row.numero_guia || String(id),
      numero_guia: existing.numero_guia || row.numero_guia || String(id),
      remitente_nombre: existing.remitente_nombre || row.remitente_nombre || null,
      destinatario_nombre: existing.destinatario_nombre || row.destinatario_nombre || null,
      bultos: getGuideNumericValue(row, ['bultos'], existing.bultos || 0),
      kg_reales: getGuideNumericValue(row, ['kg_reales', 'kilos'], existing.kg_reales || 0),
      m3: getGuideNumericValue(row, ['m3'], existing.m3 || 0),
      valor_declarado: existing.valor_declarado || 0,
      contrareembolso: existing.contrareembolso || 0,
    });
  });

  return Array.from(merged.values());
}
